<template>
  <div
    id="app"
    v-bind:class="`${vista} in-iframe-${inIframe} ${adobeTargetExperience}`"
  >
    <top-bar v-if="!inIframe" :theme="headerTheme"></top-bar>
    <div class="router-container">
      <wrap-container class="router-container-inner">
        <router-view />
      </wrap-container>
      <app-footer
        :layout="footerLayout"
        :theme="footerTheme"
        v-if="canShowFooter"
      >
        <legales v-if="vista === 'oferta'" />
        <p v-else v-safe-html="footerText" style="word-wrap: break-word;"></p>
      </app-footer>
    </div>

    <lottie-loader v-if="loading" />
    <embed-veridas
      v-if="config.isVeridasActive && m_preloadSdk"
      :inert="!inertMainContent"
      :valueIframe="iframeData"
      @close="closeModal"
    ></embed-veridas>
    <notification-toast />
  </div>
</template>

<script>
import AppFooter from "@/components/core/AppFooter";
import Legales from "@/components/core/Legales";
import WrapContainer from "@/components/core/Container";
import TopBar from "@/components/core/TopBar";
import LottieLoader from "@/components/core/LottieLoader";
import NotificationToast from "@/components/core/NotificationToast";
import EmbedVeridas from "@/components/core/custom/EmbedVeridas";
import { mapState } from "vuex";
import { motorTCCA } from "@/config/brands/ofertaCajaAhorro";
import { formatDinero, replaceAll } from "@/utils";

export default {
  name: "App",
  components: {
    TopBar,
    AppFooter,
    Legales,
    LottieLoader,
    WrapContainer,
    NotificationToast,
    EmbedVeridas
  },
  data() {
    return {
      vista: null,
      inIframe: true
    };
  },
  methods: {
    closeModal() {
      this.hideVeridasSdk();
    },
    formatPaquetesText(paquetesText, tipoPaquete) {
      const consumo = motorTCCA[tipoPaquete]?.consumo;

      if (tipoPaquete === "09") {
        return paquetesText.replace(/\$sin_consumo[\s\S]*?\$sin_consumo/, "");
      }

      if (consumo !== undefined) {
        paquetesText = replaceAll(paquetesText, "$sin_consumo", "");
        return paquetesText.replace("$consumo", `$${formatDinero(consumo)}`);
      }

      return paquetesText;
    }
  },
  computed: {
    ...mapState({
      iframeData: state => {
        return state.DVSIframeData;
      }
    }),
    footerText() {
      const stepConfig = this.config.pasos[this.vista];
      if (!stepConfig) {
        return null;
      }

      const {
        primerPaquete,
        tarjetaVos,
        tipoSolicitud
      } = this.$store.getters.getOferta();

      const objectText = stepConfig.footerText;
      const tiposPaquetes = ["01", "04", "08", "09"];
      const esDinamicoCA =
        typeof objectText === "object" && _BBVA_MOTOR === "ofertaCajaAhorro";

      if (!esDinamicoCA) {
        return objectText;
      }

      const tipoPaquete = primerPaquete?.tipoPaquete;
      const esPaqueteTC = tiposPaquetes.includes(tipoPaquete);

      let footText = tipoSolicitud === "V" ? "" : objectText?.cajaAhorro;

      if (footText !== "") {
        footText = `${footText}<br /><br />`;
      }

      if (esPaqueteTC) {
        return `${footText}${this.formatPaquetesText(
          objectText["paquetes"],
          tipoPaquete
        )}`;
      }

      if (tarjetaVos) {
        return `${footText}${objectText["tarjetaVos"]}`;
      }

      return footText;
    },
    loading() {
      return this.$store.state.isLoading;
    },
    canShowFooter() {
      //Valido que sea la oferta
      return (!this.loading && this.footerText) || this.vista === "oferta";
    },
    headerTheme() {
      // Options: light | dark (default)
      return this.config.pasos[this.vista]
        ? this.config.pasos[this.vista].headerTheme
        : "dark";
    },
    footerTheme() {
      return this.config.pasos[this.vista]
        ? this.config.pasos[this.vista].footerTheme
        : null;
    },
    footerLayout() {
      // Options: boxed | full (default)
      return this.config.pasos[this.vista]
        ? this.config.pasos[this.vista].footerLayout
        : null;
    },
    inertMainContent() {
      return this.m_isIframeSdkVisible;
    }
  },
  mounted() {
    this.inIframe = window.top != window.self;
    this.$store.watch(
      this.$store.getters.getRoute,
      route => {
        // Obtengo el name de la vista para agregar clase general al div#app
        this.vista = route.name;
      },
      { immediate: true }
    );
  }
};
</script>

<style>
@import "./style/base.css";
@import "./style/media.css";
@import "./style/grid.css";

#app {
  font-family: "BentonSansBBVA-Medium", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

body {
  margin: auto;
}

a {
  color: #e4e4e4;
}

.container-dark a {
  color: #070e46;
}

.router-container {
  padding-top: 90px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.router-container:has(.thankyou-container) {
  background: #043263;
}

.in-iframe-true .router-container {
  padding-top: 0 !important;
}

/* Estilos globales para cada paso usando la clase con el nombre de la ruta */
#app.gracias {
  background: #043263;
  /* HACK: Problema con overflow por no poder
  dar color de fondo al body
  doy scroll al elemento #app */
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
#app.graciasCA {
  background: #043263;
  /* HACK: Problema con overflow por no poder
  dar color de fondo al body
  doy scroll al elemento #app */
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
#app.gracias.in-iframe-true {
  position: relative !important;
}

@media screen and (min-width: 600px) {
  .router-container {
    /*El padding debe incluir la top bar + padding de contenedor y dar una distancia de 60 px
    entre el topbar y el primer título
    padding = altura top bar - 17px + 60px;
    */
    padding-top: 103px;
  }
}
</style>
